import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import {
  UserCircle,
  Crown,
  Mail,
  UserPlus,
  Trash2,
  Loader2,
  Copy,
  Check,
} from "lucide-react";
import { apiClient } from "../lib/apiClient";


export function GroupMembersTab({ group, members, user, isOwner, onDataRefresh }) {
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [memberEmail, setMemberEmail] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [roleChangeLoading, setRoleChangeLoading] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);

  const addMember = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError("");
    setInviteSuccess("");
    try {
      await apiClient.post("groupMembers", group.id, { email: memberEmail });
      setMemberEmail("");
      setAddMemberOpen(false);
      onDataRefresh();
    } catch (err) {
      if (err.response?.status === 404) {
        try {
          const res = await apiClient.post("groupInviteEmail", group.id, { email: memberEmail });
          if (res.data.added_directly) {
            setMemberEmail("");
            setAddMemberOpen(false);
            onDataRefresh();
          } else {
            setInviteSuccess(`Invitation email sent to ${memberEmail}!`);
            setMemberEmail("");
          }
        } catch (inviteErr) {
          setFormError(inviteErr.response?.data?.detail || "Failed to send invitation");
        }
      } else {
        setFormError(err.response?.data?.detail || "Failed to add member");
      }
    } finally {
      setFormLoading(false);
    }
  };

  const removeMember = async (memberId) => {
    try {
      await apiClient.delete("groupMember", group.id, memberId);
      onDataRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to remove member");
    }
  };

  const changeMemberRole = async (memberId, newRole) => {
    setRoleChangeLoading(memberId);
    try {
      await apiClient.patch("groupMemberRole", group.id, memberId, { role: newRole });
      onDataRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to change role");
    } finally {
      setRoleChangeLoading(null);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(group.invite_code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-2">
        <h2 className="font-serif text-lg sm:text-xl font-semibold text-stone-dark">Group Members</h2>
        {isOwner && (
          <Dialog open={addMemberOpen} onOpenChange={(open) => { setAddMemberOpen(open); if (!open) { setMemberEmail(""); setFormError(""); setInviteSuccess(""); } }}>
            <DialogTrigger asChild>
              <Button className="bg-sage hover:bg-sage-dark text-white rounded-full" data-testid="add-member-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif">Add Member</DialogTitle>
                <DialogDescription>Add a user to this group by their email address</DialogDescription>
              </DialogHeader>
              <form onSubmit={addMember} className="space-y-4">
                {formError && (
                  <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">{formError}</div>
                )}
                {inviteSuccess && (
                  <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="invite-success-msg">
                    <Mail className="w-4 h-4 shrink-0" />
                    {inviteSuccess}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="memberEmail">Email Address</Label>
                  <Input
                    id="memberEmail"
                    type="email"
                    placeholder="user@example.com"
                    value={memberEmail}
                    onChange={(e) => setMemberEmail(e.target.value)}
                    required
                    data-testid="member-email-input"
                  />
                  <p className="text-xs text-stone-dark/50">
                    If the user doesn't have an account, an invitation email will be sent automatically.
                  </p>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
                  </DialogClose>
                  <Button
                    type="submit"
                    className="bg-sage hover:bg-sage-dark text-white rounded-full"
                    disabled={formLoading}
                    data-testid="submit-add-member-btn"
                  >
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Member"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-3">
        {members.map((member) => (
          <Card key={member.id} className="border-stone" data-testid={`member-card-${member.id}`}>
            <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center shrink-0">
                  <UserCircle className="w-6 h-6 text-sage" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm sm:text-base text-stone-dark truncate">{member.name}</p>
                    {member.role === "owner" && (
                      <span className="flex items-center gap-1 text-xs bg-gold/20 text-gold-dark px-2 py-0.5 rounded-full">
                        <Crown className="w-3 h-3" />
                        {member.id === group.owner_id ? "Creator" : "Co-Owner"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-stone-dark/60 flex items-center gap-1 truncate">
                    <Mail className="w-3 h-3 shrink-0" />
                    {member.email}
                  </p>
                </div>
              </div>

              {isOwner && member.id !== user.id && (
                <div className="flex items-center gap-2 self-end sm:self-auto ml-auto sm:ml-0">
                  {member.id !== group.owner_id && (
                    <>
                      {member.role === "member" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-sage border-sage hover:bg-sage/10"
                          onClick={() => changeMemberRole(member.id, "owner")}
                          disabled={roleChangeLoading === member.id}
                          data-testid={`promote-member-${member.id}`}
                        >
                          {roleChangeLoading === member.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Crown className="w-4 h-4 mr-1" />
                              Make Owner
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-stone-dark/60 hover:bg-stone/50"
                          onClick={() => changeMemberRole(member.id, "member")}
                          disabled={roleChangeLoading === member.id}
                          data-testid={`demote-member-${member.id}`}
                        >
                          {roleChangeLoading === member.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "Remove Owner"
                          )}
                        </Button>
                      )}
                    </>
                  )}
                  {member.id !== group.owner_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => removeMember(member.id)}
                      data-testid={`remove-member-${member.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Invite Code Section */}
      <Card className="border-stone mt-6">
        <CardContent className="p-6">
          <h3 className="font-medium text-stone-dark mb-2">Invite Link</h3>
          <p className="text-sm text-stone-dark/60 mb-4">
            Share this code with others so they can join the group
          </p>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-stone/50 rounded-lg px-4 py-3 font-mono text-lg tracking-wider text-center">
              {group.invite_code}
            </div>
            <Button variant="outline" onClick={copyInviteCode} className="rounded-full">
              {copiedCode ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-500" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
